import { MultiPartInquiry } from '../interfaces'

/** Patch describing a single row's line-code value to write into the form. */
export interface LineCodeUpdate {
  index: number
  lineCode: string
}

/**
 * Finds the nearest previous row that has a part number and returns its
 * trimmed line code. Blank part-number rows are skipped as sources.
 *
 * @param items - Multi-part inquiry rows in display order.
 * @param currentIndex - Index of the row looking for a previous source.
 * @returns Trimmed line code from the nearest prior part-number row, or `''`.
 */
export const getPreviousLineCode = (
  items: MultiPartInquiry[],
  currentIndex: number
): string => {
  const previousWithPartNumber = items
    .slice(0, currentIndex)
    .findLast((item) => !!item.partNumber?.trim())
  return previousWithPartNumber?.lineCode?.trim() || ''
}

/**
 * When a row receives a part number, returns the update for the immediate
 * next row so it inherits this row's line code. No-op when there is no next
 * row, the current row has no line code, or the next row already has one.
 *
 * @param items - Multi-part inquiry rows in display order.
 * @param currentIndex - Index of the row that just received a part number.
 * @returns Update for the next empty line-code row, or `null` when nothing to apply.
 */
export const getNextRowLineCodeUpdate = (
  items: MultiPartInquiry[],
  currentIndex: number
): LineCodeUpdate | null => {
  const nextIndex = currentIndex + 1
  if (nextIndex >= items.length) {
    return null
  }

  const lineCode = items[currentIndex]?.lineCode?.trim() || ''
  if (!lineCode) {
    return null
  }

  if (items[nextIndex]?.lineCode?.trim()) {
    return null
  }

  return { index: nextIndex, lineCode }
}

/**
 * Prefills the line code for a newly appended row from the nearest
 * previous row that already has a part number.
 *
 * @param items - Existing multi-part inquiry rows before the new row is added.
 * @returns Trimmed line code to seed the new row, or `''` when none is available.
 */
export const getLineCodeForNewRow = (items: MultiPartInquiry[]): string => {
  return getPreviousLineCode(items, items.length)
}
